import { FC, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { Box } from '@mui/system';

import useDragDrop from '@/shared/hooks/useDragDrop';

type WaveUIDraggableOverlayProps = {
  children: ReactNode;
  title?: string;
  initialPos?: { x: number; y: number };
  onClose?: () => void;
  getDragging?: (dragging: boolean) => void;
};

const WaveUIDraggableOverlay: FC<WaveUIDraggableOverlayProps> = ({
  children,
  title,
  initialPos,
  onClose,
  getDragging,
}) => {
  const { t } = useTranslation(['common']);
  const containerRef = useRef<HTMLDivElement>(null);
  const { handleMouseDown, dragging } = useDragDrop({
    containerRef,
    initialPos,
    getDragging,
  });

  return (
    <Paper
      elevation={6}
      ref={containerRef}
      tabIndex={0}
      sx={{
        'position': 'absolute',
        'zIndex': dragging ? 'tooltip' : 'modal',

        '&:focus': {
          outline: 'none',
          zIndex: 'tooltip',
        },
      }}
      style={{
        left: initialPos?.x,
        top: initialPos?.y,
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          p: 2,
          pb: 3,
        }}
      >
        <Typography variant='h6'>{title}</Typography>
        <IconButton
          onMouseDown={handleMouseDown}
          disableRipple
          size='small'
          sx={{
            'minWidth': 'max-content',
            'cursor': 'grab',

            '&:active': {
              cursor: 'grabbing',
            },
          }}
        >
          <DragIndicatorIcon />
        </IconButton>
      </Stack>
      {children}

      <Box sx={{ px: 2, pb: 3 }}>
        <Button variant='contained' fullWidth onClick={onClose}>
          {t('common:close')}
        </Button>
      </Box>
    </Paper>
  );
};

export default WaveUIDraggableOverlay;
